import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { PageHeader, Table, Badge } from "@/components/erp/ui";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, MessageCircle } from "lucide-react";

const WaReminders = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/invoices/wa-reminders").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d || !d.rows.length) return null;
  const send = async (r) => { window.open(r.wa_url, "_blank", "noopener"); try { await api.post(`/invoices/${r.id}/wa-reminded`); load(); } catch { /* ignore */ } };
  return (
    <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-5" data-testid="wa-reminders">
      <div className="flex items-center gap-2 mb-1"><MessageCircle className="w-4 h-4 text-rose-600" /><b className="text-sm text-rose-800">Pengingat pelunasan via WA ({d.rows.length})</b></div>
      <p className="text-xs text-rose-700 mb-3">Invoice yang sudah lewat {d.min_days} hari atau lebih dari jatuh tempo. Klik “Kirim WA” — pesan sudah terisi, tinggal tekan kirim di WhatsApp.</p>
      <div className="space-y-2">{d.rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-rose-100 px-3 py-2 text-sm" data-testid={`wa-rem-${r.code}`}>
          <span className="font-mono text-xs">{r.code}</span><span className="font-medium">{r.customer_name}</span>
          <span className="text-xs text-rose-600">lewat {r.days} hari</span><span className="font-semibold">{formatIDR(r.outstanding)}</span>
          {r.wa_reminded_at && <span className="text-[11px] text-slate-500" data-testid={`wa-rem-last-${r.code}`}>terakhir diingatkan {new Date(r.wa_reminded_at).toLocaleString("id-ID")} oleh {r.wa_reminded_by}</span>}
          <span className="ml-auto">{r.wa_url ? <button type="button" data-testid={`wa-rem-send-${r.code}`} onClick={() => send(r)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700"><MessageCircle className="w-3.5 h-3.5" /> Kirim WA</button> : <span className="text-xs text-amber-700" data-testid={`wa-rem-nophone-${r.code}`}>No. HP customer belum diisi</span>}</span>
        </div>))}</div>
    </div>
  );
};

const BUCKETS = [
  ["not_due", "Belum Jatuh Tempo", "text-slate-700", "bg-slate-100 text-slate-600"],
  ["b0_30", "Terlambat 0–30 hari", "text-amber-600", "bg-amber-100 text-amber-700"],
  ["b31_60", "Terlambat 31–60 hari", "text-orange-600", "bg-orange-100 text-orange-700"],
  ["b60p", "Terlambat 60+ hari", "text-rose-600", "bg-rose-100 text-rose-700"],
];

export default function Receivables() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();
  const loadAging = () => api.get("/invoices/aging").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { loadAging(); }, []);
  const remind = async (k) => {
    setBusy(k);
    try { const { data } = await api.post(`/invoices/aging/remind?bucket=${k}`); toast.success(`Pengingat terkirim: ${data.sent} · dilewati (tanpa email): ${data.skipped}`); loadAging(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim pengingat"); }
    setBusy("");
  };

  const totals = data?.totals || {};
  const allRows = data ? BUCKETS.flatMap(([k]) => (data.buckets[k] || []).map((r) => ({ ...r, bucket: k }))) : [];
  allRows.sort((a, b) => (b.days || -999) - (a.days || -999));
  const bucketOf = (k) => BUCKETS.find((b) => b[0] === k);

  return (
    <div>
      <PageHeader title="Laporan Piutang (Aging)" subtitle="Tagihan invoice belum lunas dikelompokkan per rentang keterlambatan." />
      <WaReminders />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="aging-cards">
        {BUCKETS.map(([k, label, accent]) => {
          const n = (data?.buckets[k] || []).length;
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col" data-testid={`aging-card-${k}`}>
              <div className="text-xs text-slate-500">{label}</div>
              <div className={`text-xl font-head font-bold mt-1 ${accent}`}>{formatIDR(totals[k] || 0)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{n} invoice</div>
              {k !== "not_due" && n > 0 && (
                <button data-testid={`aging-remind-${k}`} disabled={busy === k} onClick={() => remind(k)}
                  className="mt-2 inline-flex items-center justify-center gap-1 text-xs font-medium px-2 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-sky-400 hover:text-sky-600 disabled:opacity-50">
                  <Bell className="w-3.5 h-3.5" /> {busy === k ? "Mengirim…" : "Kirim Pengingat"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex items-center justify-between" data-testid="aging-total">
        <span className="text-sm text-slate-500">Total Piutang Belum Lunas</span>
        <span className="text-2xl font-head font-bold text-slate-900">{formatIDR(totals.total || 0)}</span>
      </div>

      <Table testid="aging-table" columns={["Kode", "Customer", "Jatuh Tempo", "Keterlambatan", "Rentang", "Sisa Tagihan", "Status"]} rows={allRows} empty="Tidak ada piutang — semua invoice lunas 🎉" renderRow={(r) => {
        const b = bucketOf(r.bucket);
        return (
          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`aging-row-${r.code}`} onClick={() => nav("/app/invoices")}>
            <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
            <td className="px-4 py-3">{r.customer_name}{r.company ? <div className="text-xs text-slate-400">{r.company}</div> : null}</td>
            <td className="px-4 py-3 text-slate-600">{r.due_date || "-"}</td>
            <td className="px-4 py-3">{r.days == null ? <span className="text-slate-400 text-xs">-</span> : r.days <= 0 ? <span className="text-slate-400 text-xs">belum</span> : <span className="text-slate-700 text-xs font-medium">{r.days} hari</span>}</td>
            <td className="px-4 py-3"><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${b[3]}`}>{b[1]}</span></td>
            <td className="px-4 py-3 font-semibold">{formatIDR(r.outstanding)}</td>
            <td className="px-4 py-3"><Badge>{r.status}</Badge></td>
          </tr>
        );
      }} />
    </div>
  );
}
